import { useEffect, useRef, useState } from 'react'
import { backgroundImage } from '@/lib/format'
import { cn } from '@/lib/cn'
import { useAutoResize } from '@/hooks/use-auto-resize'
import { Media } from '@/models/media'
import { giveawayUrl, normalizeSlugInput, slugError, slugify } from '@/lib/slug'
import { SlugField } from '../giveaway/SlugField'
import { MediaPickerOverlay } from '../MediaPickerOverlay'
import { GalleryEditor, VideoField } from '../media/GalleryEditor'
import { ToggleField } from '../ui/ToggleField'
import { videoFieldValid, videoFieldValue } from '@/lib/video'
import { notifyError } from '@/lib/notify'
import type { StepProps } from './types'

/** Port of components/new_giveaway/pages/details.vue */
export function DetailsStep({ giveaway, update, onBack, onNext }: StepProps) {
  const [selectingMedia, setSelectingMedia] = useState(false)
  const textarea = useRef<HTMLTextAreaElement>(null)
  useAutoResize(textarea, giveaway.description ?? '')

  const cover = giveaway.cover_media?.src ?? null
  const gameArt = giveaway.game_info?.display_cover_media || null

  // An untouched slug tracks the name; once edited by hand it stops following.
  const slug = giveaway.slug ?? slugify(giveaway.name ?? '')

  useEffect(() => {
    if (giveaway.slug === null && giveaway.name) {
      update((draft) => {
        draft.slug = slugify(draft.name ?? '')
      })
    }
  }, [giveaway.slug, giveaway.name, update])

  return (
    <div className="content-section">
      {selectingMedia ? (
        <MediaPickerOverlay
          onClose={() => setSelectingMedia(false)}
          onSelect={(media) => {
            update((draft) => {
              draft.cover_media = media
              draft.cover_media_id = media?.id ?? null
            })
            setSelectingMedia(false)
          }}
        />
      ) : null}

      <div className="field-row">
        <div className="field">
          <div className="field-name">Cover Image / Embed</div>
          <div
            className={cn('media-field background-cover', cover && 'filled')}
            style={backgroundImage(cover)}
          >
            {!cover ? (
              <div className="media-field-content">
                <div className="content-row button">
                  <img src="/icons/add_cover.png" alt="" />
                </div>
                <button
                  type="button"
                  className="content-row upper button"
                  onClick={() => setSelectingMedia(true)}
                >
                  Choose cover image
                </button>
                <div className="divider-row upper">OR</div>
                <button
                  type="button"
                  className="content-row upper button"
                  disabled={!gameArt}
                  onClick={() =>
                    update((draft) => {
                      // The original rendered this option but never wired it up.
                      draft.cover_media = gameArt ? new Media({ src: gameArt }) : null
                      draft.cover_media_id = null
                    })
                  }
                >
                  Use Default Game Image
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Defaults to the giveaway's name; the organiser can set it explicitly
          before the link ever gets shared. */}
      <SlugField
        slug={slug}
        onChange={(value) =>
          update((draft) => {
            draft.slug = normalizeSlugInput(value)
          })
        }
        onReset={() =>
          update((draft) => {
            draft.slug = slugify(draft.name ?? '')
          })
        }
        error={slug.length > 0 ? slugError(slug) : null}
        previewUrl={giveawayUrl(slug || 'your-giveaway')}
      />

      <div className="field-row">
        <div className="field">
          <div className="field-name">Description</div>
          <textarea
            ref={textarea}
            className="description-field"
            placeholder="Type description here..."
            aria-label="Giveaway description"
            value={giveaway.description ?? ''}
            onChange={(event) =>
              update((draft) => {
                draft.description = event.target.value
              })
            }
          />
          <div className="field-hint">
            Web addresses become links. To name one, write [text](https://…).
          </div>
        </div>
      </div>

      <VideoField
        label="Featured video (optional)"
        value={giveaway.video_url ?? ''}
        onChange={(value) =>
          update((draft) => {
            draft.video_url = value
          })
        }
        hint={
          giveaway.game_info?.video_url
            ? "Leave empty to show the game's video."
            : 'A YouTube or Vimeo link. Optional.'
        }
      />

      <GalleryEditor
        items={giveaway.gallery}
        onChange={(items) =>
          update((draft) => {
            draft.gallery = items
          })
        }
        hint={
          giveaway.game_info && giveaway.game_info.gallery.length > 0
            ? "Leave empty to show the game's gallery."
            : undefined
        }
      />

      <ToggleField
        label="Hide the number of entries"
        checked={giveaway.hide_entries}
        onChange={(checked) =>
          update((draft) => {
            draft.hide_entries = checked
          })
        }
        hint="Only you will see how many entries it has."
      />

      <div className="footer">
        <div className="cta-row">
          <button type="button" className="cta" onClick={onBack}>
            Back
          </button>
          <button
            type="button"
            className="cta next"
            onClick={() => {
              const raw = giveaway.video_url ?? ''
              if (!videoFieldValid(raw)) {
                notifyError('The featured video needs a YouTube or Vimeo link, or leave it empty.')
                return
              }
              update((draft) => {
                draft.video_url = videoFieldValue(raw)
              })
              onNext()
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}
